import { datadogRum } from '@datadog/browser-rum';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { ehErroHttp } from '@/shared/utils/erroHttp';
import { reportError } from '@/shared/utils/reportError';

import type {
  ExecucaoEmErro,
  TentarDeNovoArgs,
  TentarDeNovoResultado,
} from '../../types/tentarDeNovo';
import { motivoAcaoLegivel } from '../../utils/motivoLoteLabels';
import { tentarDeNovoService } from './services';

export const fetchExecucoesEmErro = createAsyncThunk<
  ExecucaoEmErro[],
  string,
  { rejectValue: string }
>('secretariaArbitralTentarDeNovo/listar', async (processoId, { rejectWithValue }) => {
  try {
    return await tentarDeNovoService.listarExecucoesEmErro(processoId);
  } catch (error: unknown) {
    return rejectWithValue(motivoAcaoLegivel(error, 'Erro ao carregar a etapa que falhou'));
  }
});

/**
 * O motivo é texto livre sobre o caso (LGPD): a telemetria leva só o desfecho, a classe e o tipo
 * do bloco. `FALHOU_DE_NOVO` é 200 (a retentativa aconteceu e está na trilha), e a recusa 4xx é a
 * frase que o diálogo mostra: nenhum dos dois vira erro no RUM, ou o alarme de falha dispararia a
 * cada carência de 2 minutos. Só 5xx e falha de rede viram erro.
 */
export const tentarDeNovo = createAsyncThunk<
  TentarDeNovoResultado,
  TentarDeNovoArgs,
  { rejectValue: string }
>('secretariaArbitralTentarDeNovo/tentar', async (args, { rejectWithValue }) => {
  const { classe, nodeType } = args.telemetria;
  let resultado: TentarDeNovoResultado;
  try {
    resultado = await tentarDeNovoService.tentarDeNovo(args);
  } catch (error: unknown) {
    if (ehErroHttp(error) && error.status < 500) {
      datadogRum.addAction('tentar_de_novo', {
        resultado: 'RECUSADA',
        classe,
        nodeType,
        statusHttp: error.status,
        codigo: error.codigo,
      });
    } else {
      reportError(error instanceof Error ? error : new Error(motivoAcaoLegivel(error)), {
        source: 'tentar-de-novo',
        processoArbitralId: args.processoId,
        classe,
        nodeType,
        statusHttp: ehErroHttp(error) ? error.status : null,
      });
    }
    return rejectWithValue(motivoAcaoLegivel(error, 'Erro ao tentar de novo'));
  }
  datadogRum.addAction('tentar_de_novo', { resultado: resultado.resultado, classe, nodeType });
  return resultado;
});
